import React, { useEffect, useState } from "react";
import { classes } from "@bloomberg/media-ui-core";
import { SecurityList, Variant } from "@bloomberg/media-ui-markets";
import { TipKey, TipVariant, useTippy } from "@bloomberg/tippy";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import { useQuickAdd } from "@foundation/quickadd-provider";
import { useFeatureGate } from "@foundation/statsig";

import { BLENS_ARTICLE_TYPES } from "~constants";
import { fetchBlensTickersByQuoteIds } from "~api/client/fetchBlensTickersByQuoteIds";
import BrokerBoxArticleAd from "~components/BrokerBoxArticleAd";
import { ARTICLE_TYPE, BlensTickers } from "~types/types";

import styles from "./styles.module.css";

type BlensComponentProps = {
    className?: string;
    wrap?: boolean;
    quoteIds: string[];
};

export function tickersAreEqual(prevProps?: any, nextProps?: any) {
    return JSON.stringify(prevProps) === JSON.stringify(nextProps);
}
const MemoizedSecurityList = React.memo(SecurityList, tickersAreEqual);
const trackingFunction = (eventAction: string, eventLabel: string) => {
    trackClickEvent("Watchlist", eventLabel, eventAction, "Watchlist");
};

const securityTrackingFunction = (tickerId: string, _: string) => {
    // track user clicks a ticker pill on BLENS
    trackClickEvent("BLENS", "BLENS-click", "Click", "Article");

    // track user clicks a specific ticker pill on BLENS
    trackClickEvent("BLENS", `BLENS-click-${tickerId}`, "Click", "Article");
};

export function Blens({ className, wrap, quoteIds }: BlensComponentProps) {
    const { controller: qac } = useQuickAdd();
    const [tickers, setTickers] = useState<BlensTickers>([]);
    const featureGate = useFeatureGate("watchlist_paywall");

    const [quickAddWatchlist, setQuickAddWatchlist] = useState<Element | null>(
        null
    );
    useTippy({
        tipKey: TipKey.QUICKADD_WATCHLIST,
        variant: TipVariant.POPOVER,
        refNode: quickAddWatchlist,
        additionalSelectorPath: "[data-component=icon-button]",
    });

    useEffect(() => {
        if (!quoteIds || !quoteIds.length) return undefined;
        fetchBlensTickersByQuoteIds(quoteIds).then(setTickers);
        if (qac) {
            quoteIds.forEach((ticker) => {
                qac.registerTicker(ticker);
            });
            qac.updateTickerWatchlists();
        }
        return undefined;
    }, [quoteIds, qac]);

    if (!tickers.length) return null;

    return (
        <MemoizedSecurityList
            securities={tickers}
            className={classes([className, styles.blens])}
            title="In this Article"
            wrap={wrap}
            columns={2}
            variant={Variant.refresh2024}
            securityTrackClick={securityTrackingFunction}
            quickAdd
            quickAddTrackClick={trackingFunction}
            ref={setQuickAddWatchlist}
            disabled={!Boolean(featureGate.value)}
        />
    );
}

interface BlensWithBrokerAdProps extends BlensComponentProps {
    storyType?: string;
}

export function BlensWithBrokerAd({
    storyType,
    ...props
}: BlensWithBrokerAdProps) {
    if (
        !props.quoteIds?.length ||
        !storyType ||
        !BLENS_ARTICLE_TYPES[storyType as ARTICLE_TYPE]
    ) {
        return null;
    }

    return (
        <>
            <Blens {...props} />
            <BrokerBoxArticleAd />
        </>
    );
}
